import React, { useMemo } from 'react';

import ProcurementContext from './ProcurementContext';

const ProcurementProvider = ({
    // Settings
    groups,
    users,
    departments,
    isPrEnabled,
    isEaEnabled,
    isDirectCommitEnabled,
    isProcurementPlanModuleEnabled,
    isReportPurchaseOrdersModuleEnabled,
    isWorkforceModuleEnabled,
    isSupplierPortalEnabled,
    isProcurementRedesignEnabled,
    currencySymbol,
    approvalStatuses,
    pendingStatus,
    cancelledStatus,
    approvalPOStatuses,
    pendingPOStatus,
    isIntegrationConfigured,
    isSyncEnabled,
    processedActionSyncSuppliers,
    processedModelSupplier,
    activeTabId,
    openRecord,
    eventStartDate,
    eventEndDate,
    departmentsOptions,
    revDepartmentsOptions,
    ownersOptions,
    departmentContextId,
    authUserId,
    eventSlug,

    // Routes
    fetchProcurementStatsUrl,
    approvalsSummaryUrl,
    fetchHighPriorityProcurementsUrl,
    fetchProcurementPlanUrl,
    fetchProcurementPlanItemUrl,
    fetchProcurementPlanItemsUrl,
    downloadProcurementPlanItemAttachmentUrl,
    fetchProcurementKanbanBoardDataUrl,
    purchaseRequisitionsExportUrl,
    fetchOpenedPurchaseRequisitions,
    fetchApprovedPurchaseRequisitions,
    fetchRejectedPurchaseRequisitions,
    fetchCancelledPurchaseRequisitions,
    updatePurchaseRequisitionUrl,
    deletePurchaseRequisitionUrl,
    togglePurchaseRequisitionUrl,
    createPurchaseOrderUrl,
    purchaseOrdersExportUrl,
    fetchPurchaseOrderUrl,
    updatePurchaseOrderUrl,
    deletePurchaseOrderUrl,
    cancelPurchaseOrderUrl,
    togglePurchaseOrderUrl,
    fetchApprovedPurchaseOrdersUrl,
    fetchRejectedPurchaseOrdersUrl,
    fetchCanceledPurchaseOrdersUrl,
    fetchPoliciesUrl,
    createPolicyUrl,
    updatePolicyUrl,
    deletePolicyUrl,
    fetchCategoriesUrl,
    updateCategoryUrl,
    createCategoryUrl,
    deleteCategoryUrl,
    fetchSupplierContractsTermOverduesIndexUrl,
    fetchContractStatsUrl,
    fetchContractDashboardUrl,
    updateContractEndingSoonWindowUrl,
    updateSignedContractUrl,
    listUsersUrl,
    toggleSyncErpUrl,
    syncErpNowUrl,
    syncProcurementPlanProjectsUrl,
    fetchDepartmentsUrl,
    refreshCommitmentDataUrl,
    directPurchaseOrderUrl,
    storePRUrl,
    indexPRUrl,
    fetchExpensesUrl,
    directCommitmentUrl,
    storeProcurementPlanItemUrl,
    workforceContractBulkDeleteUrl,
    revenueContractBulkDeleteUrl,
    // IB-610: Saved Drafts table on the Plan tab.
    draftsIndexUrl,
    deleteDraftUrl,

    // other props
    children,
}) => {
    const settings = useMemo(
        () => ({
            groups,
            users,
            departments,
            isPrEnabled,
            isEaEnabled,
            isDirectCommitEnabled,
            isProcurementPlanModuleEnabled,
            isReportPurchaseOrdersModuleEnabled,
            isWorkforceModuleEnabled,
            isSupplierPortalEnabled,
            isProcurementRedesignEnabled,
            currencySymbol,
            approvalStatuses,
            pendingStatus,
            cancelledStatus,
            approvalPOStatuses,
            pendingPOStatus,
            isIntegrationConfigured,
            isSyncEnabled,
            processedActionSyncSuppliers,
            processedModelSupplier,
            activeTabId,
            openRecord,
            eventStartDate,
            eventEndDate,
            departmentsOptions,
            revDepartmentsOptions,
            ownersOptions,
            departmentContextId,
            authUserId,
            eventSlug,
        }),
        [
            groups,
            users,
            departments,
            isPrEnabled,
            isEaEnabled,
            isDirectCommitEnabled,
            isProcurementPlanModuleEnabled,
            isReportPurchaseOrdersModuleEnabled,
            isWorkforceModuleEnabled,
            isSupplierPortalEnabled,
            isProcurementRedesignEnabled,
            currencySymbol,
            approvalStatuses,
            pendingStatus,
            cancelledStatus,
            approvalPOStatuses,
            pendingPOStatus,
            isIntegrationConfigured,
            isSyncEnabled,
            processedActionSyncSuppliers,
            processedModelSupplier,
            activeTabId,
            openRecord,
            eventStartDate,
            eventEndDate,
            departmentsOptions,
            revDepartmentsOptions,
            ownersOptions,
            departmentContextId,
            authUserId,
            eventSlug,
        ],
    );

    const routes = useMemo(
        () => ({
            fetchProcurementStatsUrl,
            approvalsSummaryUrl,
            fetchHighPriorityProcurementsUrl,
            fetchProcurementPlanUrl,
            fetchProcurementPlanItemUrl,
            fetchProcurementPlanItemsUrl,
            downloadProcurementPlanItemAttachmentUrl,
            fetchProcurementKanbanBoardDataUrl,
            purchaseRequisitionsExportUrl,
            fetchOpenedPurchaseRequisitions,
            fetchApprovedPurchaseRequisitions,
            fetchRejectedPurchaseRequisitions,
            fetchCancelledPurchaseRequisitions,
            updatePurchaseRequisitionUrl,
            deletePurchaseRequisitionUrl,
            togglePurchaseRequisitionUrl,
            createPurchaseOrderUrl,
            purchaseOrdersExportUrl,
            fetchPurchaseOrderUrl,
            updatePurchaseOrderUrl,
            deletePurchaseOrderUrl,
            cancelPurchaseOrderUrl,
            togglePurchaseOrderUrl,
            fetchApprovedPurchaseOrdersUrl,
            fetchRejectedPurchaseOrdersUrl,
            fetchCanceledPurchaseOrdersUrl,
            fetchPoliciesUrl,
            createPolicyUrl,
            updatePolicyUrl,
            deletePolicyUrl,
            fetchCategoriesUrl,
            updateCategoryUrl,
            createCategoryUrl,
            deleteCategoryUrl,
            fetchSupplierContractsTermOverduesIndexUrl,
            fetchContractStatsUrl,
            fetchContractDashboardUrl,
            updateContractEndingSoonWindowUrl,
            updateSignedContractUrl,
            toggleSyncErpUrl,
            syncErpNowUrl,
            listUsersUrl,
            syncProcurementPlanProjectsUrl,
            fetchDepartmentsUrl,
            storeProcurementPlanItemUrl,
            refreshCommitmentDataUrl,
            directPurchaseOrderUrl,
            storePRUrl,
            indexPRUrl,
            fetchExpensesUrl,
            directCommitmentUrl,
            draftsIndexUrl,
            deleteDraftUrl,
            workforceContractBulkDeleteUrl,
            revenueContractBulkDeleteUrl,
        }),
        [
            fetchProcurementStatsUrl,
            approvalsSummaryUrl,
            fetchHighPriorityProcurementsUrl,
            fetchProcurementPlanUrl,
            fetchProcurementPlanItemUrl,
            fetchProcurementPlanItemsUrl,
            downloadProcurementPlanItemAttachmentUrl,
            fetchProcurementKanbanBoardDataUrl,
            purchaseRequisitionsExportUrl,
            fetchOpenedPurchaseRequisitions,
            fetchApprovedPurchaseRequisitions,
            fetchRejectedPurchaseRequisitions,
            fetchCancelledPurchaseRequisitions,
            updatePurchaseRequisitionUrl,
            deletePurchaseRequisitionUrl,
            togglePurchaseRequisitionUrl,
            createPurchaseOrderUrl,
            purchaseOrdersExportUrl,
            fetchPurchaseOrderUrl,
            updatePurchaseOrderUrl,
            deletePurchaseOrderUrl,
            cancelPurchaseOrderUrl,
            togglePurchaseOrderUrl,
            fetchApprovedPurchaseOrdersUrl,
            fetchRejectedPurchaseOrdersUrl,
            fetchCanceledPurchaseOrdersUrl,
            fetchPoliciesUrl,
            createPolicyUrl,
            updatePolicyUrl,
            deletePolicyUrl,
            fetchCategoriesUrl,
            updateCategoryUrl,
            createCategoryUrl,
            deleteCategoryUrl,
            fetchSupplierContractsTermOverduesIndexUrl,
            fetchContractStatsUrl,
            fetchContractDashboardUrl,
            updateContractEndingSoonWindowUrl,
            updateSignedContractUrl,
            toggleSyncErpUrl,
            syncErpNowUrl,
            listUsersUrl,
            syncProcurementPlanProjectsUrl,
            fetchDepartmentsUrl,
            storeProcurementPlanItemUrl,
            refreshCommitmentDataUrl,
            directPurchaseOrderUrl,
            storePRUrl,
            indexPRUrl,
            fetchExpensesUrl,
            directCommitmentUrl,
            draftsIndexUrl,
            deleteDraftUrl,
            workforceContractBulkDeleteUrl,
            revenueContractBulkDeleteUrl,
        ],
    );

    const value = { settings, routes };

    return <ProcurementContext.Provider value={value}>{children}</ProcurementContext.Provider>;
};

export default ProcurementProvider;
